import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useActionLock } from '@breaker/action-lock';
import { api, ApiError } from '@/auth/api';
import { authService, useAuthState, type AuthTokens, type AuthUser } from '@/auth/hooks';
import { tokenStore } from '@/auth/token-store';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';

/**
 * /join — the web door of the one invite rail (Team unit). The email's link
 * lands here with ?code=; the same code also types in by hand. Signed-in
 * users redeem straight into the fleet; new people go email → 6-digit
 * sign-in code → name, and the completion seats their membership with the
 * badges the invite carries. White-label: this page renders on tenant admin
 * hosts too, so the fleet's name (from the code preview) is the headline.
 */

function mintHandle(email: string): string {
  const base = (email.split('@')[0] ?? 'member')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 12);
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base || 'member'}${suffix}`;
}

type Stage = 'code' | 'email' | 'otp' | 'name';

export function JoinPage() {
  const search = useSearch({ from: '/join' });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();

  const [stage, setStage] = useState<Stage>('code');
  const [code, setCode] = useState(search.code ?? '');
  const [fleetName, setFleetName] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submitLock = useActionLock();

  function fail(err: unknown, fallback: string) {
    setError(err instanceof ApiError ? err.message : fallback);
  }

  async function redeemAndEnter(): Promise<void> {
    const { fleet } = await api<{ fleet: { id: string } }>('/v1/me/fleets/redeem-code', {
      method: 'POST',
      body: { code: code.trim() },
    });
    void fleet;
    await navigate({ to: '/' });
  }

  function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      setError('Enter the invite code from your email.');
      return;
    }
    submitLock.run(async () => {
      try {
        // Public preview validates the code and names the fleet.
        const preview = await api<{ fleetName: string }>(
          `/v1/auth/join-code/${encodeURIComponent(trimmed)}`,
          { unauthenticated: true },
        );
        setFleetName(preview.fleetName);
        if (isAuthenticated) {
          await redeemAndEnter();
        } else {
          setStage('email');
        }
      } catch (err) {
        fail(err, "That code isn't live — check it with whoever invited you.");
      }
    });
  }

  function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Enter the email address your invite was sent to.');
      return;
    }
    submitLock.run(async () => {
      try {
        await authService.otpRequest(email.trim());
        setStage('otp');
      } catch (err) {
        fail(err, 'Could not send the sign-in code. Try again in a moment.');
      }
    });
  }

  function onSubmitOtp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!otp.trim()) {
      setError('Enter the 6-digit code we emailed you.');
      return;
    }
    submitLock.run(async () => {
      try {
        // Raw verify (not authService.otpVerify): a brand-new account needs
        // the signupToken so completion can seat the membership.
        const res = await api<
          | { status: 'signed_in'; user: AuthUser; tokens: AuthTokens }
          | { status: 'signup'; signupToken: string }
        >('/v1/auth/otp/verify', {
          method: 'POST',
          body: { email: email.trim(), code: otp.trim() },
          unauthenticated: true,
        });
        if (res.status === 'signed_in') {
          tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
          tokenStore.setRefresh(res.tokens.refreshToken);
          await redeemAndEnter();
        } else {
          setSignupToken(res.signupToken);
          setStage('name');
        }
      } catch (err) {
        fail(err, "That code didn't work — check it and try again.");
      }
    });
  }

  function onSubmitName(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Enter your name.');
      return;
    }
    submitLock.run(async () => {
      try {
        const res = await api<{ user: AuthUser; tokens: AuthTokens }>('/v1/auth/otp/complete', {
          method: 'POST',
          body: {
            signupToken,
            handle: mintHandle(email),
            displayName: name.trim(),
            joinCode: code.trim(),
          },
          unauthenticated: true,
        });
        tokenStore.setAccess(res.tokens.accessToken, res.tokens.accessTokenExpiresAt);
        tokenStore.setRefresh(res.tokens.refreshToken);
        await navigate({ to: '/' });
      } catch (err) {
        fail(err, 'Could not finish joining. Try again in a moment.');
      }
    });
  }

  const forms: Record<Stage, { onSubmit: (e: FormEvent) => void; body: React.ReactNode; cta: string }> = {
    code: {
      onSubmit: onSubmitCode,
      cta: 'Continue',
      body: (
        <Field label="Invite code" htmlFor="joinCode" hint="From your invite email.">
          <Input
            id="joinCode"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ABCD1234"
            autoFocus
          />
        </Field>
      ),
    },
    email: {
      onSubmit: onSubmitEmail,
      cta: 'Email me a sign-in code',
      body: (
        <Field
          label="Email"
          htmlFor="joinEmail"
          hint="Use the address your invite was sent to."
        >
          <Input
            id="joinEmail"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoFocus
          />
        </Field>
      ),
    },
    otp: {
      onSubmit: onSubmitOtp,
      cta: 'Verify',
      body: (
        <Field label="Sign-in code" htmlFor="joinOtp" hint={`We emailed a 6-digit code to ${email.trim()}.`}>
          <Input
            id="joinOtp"
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="123456"
            autoFocus
          />
        </Field>
      ),
    },
    name: {
      onSubmit: onSubmitName,
      cta: 'Join',
      body: (
        <Field label="Your name" htmlFor="joinName">
          <Input
            id="joinName"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sam Carter"
            autoFocus
          />
        </Field>
      ),
    },
  };

  const current = forms[stage];

  return (
    <div className="min-h-screen flex flex-col bg-breaker-ground">
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-10 text-center">
            <span className="text-3xl font-semibold tracking-tight text-breaker-ink">
              {fleetName ?? 'Join a fleet'}
            </span>
            <p className="text-sm text-breaker-mute mt-2">
              {fleetName ? 'has invited you to their team' : 'Enter your invite code to get started'}
            </p>
          </div>

          <form onSubmit={current.onSubmit} className="space-y-4">
            {current.body}
            {error ? (
              <div className="rounded-md bg-breaker-red/10 border border-breaker-red/30 px-3 py-2 text-sm text-breaker-red-deep">
                {error}
              </div>
            ) : null}
            <Button type="submit" className="w-full" size="lg" loading={submitLock.locked}>
              {current.cta}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-breaker-mute">
            Already have an account?{' '}
            <Link to="/login" className="text-breaker-identity hover:underline">
              Sign in
            </Link>{' '}
            first and come back with your code.
          </p>
        </div>
      </main>
    </div>
  );
}
